import { Link, useParams } from 'react-router-dom';
import { getArticleBySlug, getRelatedArticles, articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { usePageSEO } from '@/hooks/usePageSEO';
import { getArticleSchema, getWebPageSchema, getBreadcrumbSchema, getOrganizationSchema, siteDomain } from '@/utils/schema';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  usePageSEO({
    title: article ? `${article.title} - kaiyuntiyu` : '文章 - kaiyuntiyu',
    description: article?.excerpt || '浏览体育资讯文章',
    keywords: article?.tags?.join(',') || 'kaiyuntiyu,体育资讯',
    canonicalUrl: `${siteDomain}/article/${slug || ''}`,
    ogType: 'article',
    ogImage: article?.image,
    schema: article
      ? [
          getOrganizationSchema(),
          getWebPageSchema(article.title, article.excerpt, `${siteDomain}/article/${article.slug}`),
          getArticleSchema(article),
          getBreadcrumbSchema([
            { name: '首页', url: siteDomain },
            { name: '全部分类', url: `${siteDomain}/category` },
            { name: categories.find((c) => c.slug === article.category)?.name || '分类', url: `${siteDomain}/category/${article.category}` },
            { name: article.title, url: `${siteDomain}/article/${article.slug}` },
          ]),
        ]
      : undefined,
  });

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 text-center py-20">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-primary-100/70 rounded-2xl">
            <i className="ri-article-line text-primary-600 text-2xl"></i>
          </div>
          <h1 className="text-xl font-bold text-foreground-950 font-heading mb-3">文章未找到</h1>
          <p className="text-sm text-foreground-500 mb-6">该文章不存在或已被移除</p>
          <Link to="/category" className="inline-flex items-center gap-1 px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-lg hover:bg-primary-600 transition-all no-underline whitespace-nowrap">
            <i className="ri-arrow-left-line"></i> 返回资讯列表
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = getRelatedArticles(article.relatedArticles || []);
  const categoryArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <div className="pt-16">
        <Breadcrumb items={[
          { name: '首页', path: '/' },
          { name: category?.name || '分类', path: `/category/${article.category}` },
          { name: article.title },
        ]} />
      </div>

      <main>
        <article className="w-full py-10 md:py-14">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full whitespace-nowrap">
                  {category?.name}
                </span>
                {article.tags?.slice(0, 3).map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 font-heading leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-6 text-sm text-foreground-500">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full overflow-hidden bg-background-200">
                    <img src={article.authorAvatar} alt={article.author} className="w-full h-full object-cover" />
                  </div>
                  <span className="font-medium text-foreground-700">{article.author}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i> {article.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i> {article.readTime}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i> {(article.views / 1000).toFixed(1)}k 阅读
                  </span>
                </div>
              </div>
            </div>

            <div className="relative w-full h-[300px] md:h-[420px] rounded-xl overflow-hidden mb-10">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
                [&_h3]:text-xl [&_h3]:font-bold [&_h3]:text-foreground-950 [&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:font-heading
                [&_p]:mb-4 [&_p]:leading-relaxed [&_p]:text-foreground-700
                [&_strong]:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            ></div>

            {article.tags && article.tags.length > 0 && (
              <div className="flex items-center gap-2 mt-10 pt-8 border-t border-background-200/70 flex-wrap">
                <span className="text-sm font-medium text-foreground-600 whitespace-nowrap">标签：</span>
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1.5 bg-background-100 text-foreground-600 text-xs rounded-lg border border-background-200/70 whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>
            )}

            <div className="flex items-center gap-3 mt-8 pt-6 border-t border-background-200/70">
              <span className="text-sm font-medium text-foreground-600 whitespace-nowrap">分享：</span>
              <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 hover:bg-background-200 text-foreground-600 transition-colors cursor-pointer border-0" aria-label="分享到微信">
                <i className="ri-wechat-line text-base"></i>
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 hover:bg-background-200 text-foreground-600 transition-colors cursor-pointer border-0" aria-label="分享到微博">
                <i className="ri-weibo-line text-base"></i>
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 hover:bg-background-200 text-foreground-600 transition-colors cursor-pointer border-0" aria-label="复制链接">
                <i className="ri-link-m"></i>
              </button>
            </div>
          </div>
        </article>

        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-10 text-center">
              相关推荐
            </h2>

            {relatedArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => {
                  const cat = categories.find((c) => c.slug === ra.category);
                  return (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.slug}`}
                      className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-300 transition-all duration-300 no-underline cursor-pointer"
                    >
                      <div className="relative w-full h-44 overflow-hidden">
                        <img src={ra.image} alt={ra.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        <div className="absolute top-3 left-3">
                          <span className="px-2.5 py-1 bg-primary-500 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">
                            {cat?.name}
                          </span>
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-semibold text-foreground-900 mb-2 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors font-heading">
                          {ra.title}
                        </h3>
                        <div className="flex items-center justify-between text-xs text-foreground-500">
                          <span>{ra.author}</span>
                          <span>{ra.date}</span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {categoryArticles.map((ca) => (
                  <Link
                    key={ca.id}
                    to={`/article/${ca.slug}`}
                    className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-300 transition-all duration-300 no-underline cursor-pointer"
                  >
                    <div className="relative w-full h-44 overflow-hidden">
                      <img src={ca.image} alt={ca.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-foreground-900 mb-2 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors font-heading">
                        {ca.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}